import React, { StrictMode } from 'react'
import { Navigate, Outlet } from 'react-router'
import { useSelector } from 'react-redux'
import { RequireAuth } from '@core/Components/RequireAuth'
import { useFeature } from '@core/Utils/hooks/useFeature'
import { AppRoutes } from '@core/AppRoutes'
import { selectIsBetLoopAccessUnavailable } from '@core/Store/Config/selectors'
import { useShouldStartOnboarding } from './hooks/useShouldStartOnboarding'
import { useIsBetLoopEntitled } from './hooks/useIsBetLoopEntitled'
import { OnboardingManager } from './Components/OnboardingManager'
import { RestrictedAccess } from './Components/RestrictedAccess'

export const BetLoopRoot: React.FC = () => {
  return (
    <RequireAuth>
      <FeatureGate>
        <EntitledGate>
          <AvailabilityGate>
            <RootComponent />
          </AvailabilityGate>
        </EntitledGate>
      </FeatureGate>
    </RequireAuth>
  )
}

// ================
// Local Components
// ================

const FeatureGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isBetLoopFeatureActive = useFeature('content-hub-follow-me')
  return isBetLoopFeatureActive ? children : <Navigate to={AppRoutes.Home} />
}

const EntitledGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isEntitled = useIsBetLoopEntitled()
  return isEntitled ? children : <RestrictedAccess status='banned' />
}

const AvailabilityGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isBetLoopAccessUnavailable = useSelector(selectIsBetLoopAccessUnavailable)
  return isBetLoopAccessUnavailable ? <RestrictedAccess status='unavailable' /> : children
}

const RootComponent = () => {
  const { onboardingStatus, handleOnboardStatusSuccess } = useShouldStartOnboarding()

  if (onboardingStatus === 'checking') {
    return null
  }

  if (onboardingStatus === 'no-profile-found') {
    return <OnboardingManager onCompleteOnboarding={handleOnboardStatusSuccess} />
  }

  return (
    <StrictMode>
      <Outlet />
    </StrictMode>
  )
}
